import { useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useDrfqTakerOrderbook } from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-taker-orderbook-context';
import { blackTangaroa } from '@paradigm/design-system/src/colors';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import {
  useUMPlatformCreationDisabled,
  useUMPlatformKillDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';
import { getUserTicker } from '@paradigm/store/auth';
import { useHiddenLimitOrderFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { useTrackAction } from '@paradigm/features/src/analytics';
import {
  KillCell,
  MyAsk,
  MyBid,
  MyOrdersContainer,
  PriceCell,
  QtyCell,
} from '#/components/UnifiedRfqs/components/RfqRowBase';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import KillOrderButton from './KillOrderButton';
import { stopPropagation } from './common';
import CounterDrfqButton from './CounterDrfqButton';
import { TakerPriceButton } from './OrderPriceButton';

export default function TakerOrders() {
  const view = useDrfqTakerOrderbook();
  const drfq = useDrfqRfq();
  const isDated = isDatedRfqLegs(drfq.legs, drfq.product_codes[0]);
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const isHiddenLimitOrderEnabled = useHiddenLimitOrderFeatureFlag();
  const { openDetailsModal } = useContextMenuActions();
  const track = useTrackAction();

  // Retrieve the current user's desk
  const userDesk = useSelector(getUserTicker)!;

  const handlePriceClick = useCallback(() => {
    track('submit hidden limit order');
    openDetailsModal();
  }, [openDetailsModal, track]);

  if (!isHiddenLimitOrderEnabled) {
    return null;
  }

  switch (view.state) {
    case 'LOADING_ORDERS':
    case 'UNMATCHED_AUCTION':
    case 'MATCHED_AUCTION':
      return null;
    case 'OPEN_AUCTION': {
      const { bids, asks } = view;
      const filteredBid = bids.find(
        (bid) => bid.desk === userDesk || bid.type === 'HIDDEN',
      );
      const filteredAsk = asks.find(
        (ask) => ask.desk === userDesk || ask.type === 'HIDDEN',
      );

      const bid = filteredBid;
      const ask = filteredAsk;
      const isEmpty = bid == null && ask == null;

      if (isEmpty) {
        return (
          <QuoteContainer
            css="width: 345px; margin: auto;"
            onClick={stopPropagation()}
          >
            <CounterDrfqButton />
          </QuoteContainer>
        );
      }

      return (
        <MyOrdersContainer onClick={stopPropagation()}>
          {bid != null ? (
            <MyBid>
              <QtyCell disabled={isPlatformCreationDisabled}>
                {formatProductQty(
                  bid.quantity,
                  drfq.product_codes[0],
                  true,
                  isDated,
                )}
              </QtyCell>
              <KillCell>
                <KillOrderButton
                  disabled={isPlatformKillDisabled}
                  orderId={bid.id}
                />
              </KillCell>
              <PriceCell disabled={isPlatformCreationDisabled}>
                <TakerPriceButton
                  disabled={isPlatformCreationDisabled}
                  side="BUY"
                  order={bid}
                  onClick={handlePriceClick}
                />
              </PriceCell>
            </MyBid>
          ) : (
            <QuoteContainer gridArea="bid">
              <CounterDrfqButton />
            </QuoteContainer>
          )}
          {ask != null ? (
            <MyAsk>
              <QtyCell disabled={isPlatformCreationDisabled}>
                {formatProductQty(
                  ask.quantity,
                  drfq.product_codes[0],
                  true,
                  isDated,
                )}
              </QtyCell>
              <KillCell>
                <KillOrderButton
                  disabled={isPlatformKillDisabled}
                  orderId={ask.id}
                />
              </KillCell>
              <PriceCell disabled={isPlatformCreationDisabled}>
                <TakerPriceButton
                  disabled={isPlatformCreationDisabled}
                  side="SELL"
                  order={ask}
                  onClick={handlePriceClick}
                />
              </PriceCell>
            </MyAsk>
          ) : (
            <QuoteContainer gridArea="ask">
              <CounterDrfqButton />
            </QuoteContainer>
          )}
        </MyOrdersContainer>
      );
    }
    // no default
  }
}

const QuoteContainer = styled.div<{ gridArea?: string }>`
  ${(props) => props.gridArea != null && `grid-area: ${props.gridArea};`}
  height: 30px;
  border-radius: 3px;
  display: flex;
  width: 100%;
  background: ${blackTangaroa};
  justify-content: center;
  align-items: center;
`;
